import React, { createContext, useContext, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, CheckCircle2, Info } from 'lucide-react';

const PopupContext = createContext(null);

export const usePopup = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};

export const PopupProvider = ({ children }) => {
  const [popupState, setPopupState] = useState({
    isVisible: false,
    title: '',
    message: '',
    type: 'info', // 'info' | 'success' | 'error' | 'confirm'
    onConfirm: null,
    onCancel: null,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  const showPopup = useCallback(({ title, message, type = 'info', confirmText = 'OK' }) => {
    setPopupState({
      isVisible: true,
      title,
      message,
      type,
      confirmText,
      cancelText: null,
      onConfirm: () => setPopupState(prev => ({ ...prev, isVisible: false })),
      onCancel: null,
    });
  }, []);

  const showConfirm = useCallback(({ title, message, onConfirm, onCancel, confirmText = 'Confirm', cancelText = 'Cancel', isDestructive = false }) => {
    setPopupState({
      isVisible: true,
      title,
      message,
      type: isDestructive ? 'error' : 'confirm',
      confirmText,
      cancelText,
      onConfirm: async () => {
        setPopupState(prev => ({ ...prev, isVisible: false }));
        if (onConfirm) await onConfirm();
      },
      onCancel: () => {
        setPopupState(prev => ({ ...prev, isVisible: false }));
        if (onCancel) onCancel();
      },
    });
  }, []);

  const hidePopup = useCallback(() => {
    setPopupState(prev => ({ ...prev, isVisible: false }));
  }, []);

  return (
    <PopupContext.Provider value={{ showPopup, showConfirm, hidePopup }}>
      {children}
      <AnimatePresence>
        {popupState.isVisible && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 px-4" onClick={popupState.onCancel || hidePopup}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#111] border border-gray-800 rounded-2xl w-full max-w-sm p-6 relative overflow-hidden"
            >
              {/* Icon Header */}
              <div className="flex flex-col items-center mb-4">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mb-4 ${
                  popupState.type === 'error' ? 'bg-red-500/10' :
                  popupState.type === 'success' ? 'bg-green-500/10' :
                  'bg-[#0BD9E5]/10'
                }`}>
                  {popupState.type === 'error' && <AlertCircle size={24} className="text-red-500" />}
                  {popupState.type === 'success' && <CheckCircle2 size={24} className="text-green-500" />}
                  {(popupState.type === 'info' || popupState.type === 'confirm') && <Info size={24} className="text-[#0BD9E5]" />}
                </div>
                <h3 className="text-lg font-bold text-white text-center">{popupState.title}</h3>
              </div>

              <p className="text-gray-400 text-[15px] text-center mb-6 leading-relaxed">
                {popupState.message}
              </p>

              <div className="flex gap-3">
                {popupState.cancelText && (
                  <button
                    onClick={popupState.onCancel || hidePopup}
                    className="flex-1 px-4 py-3 bg-[#2a2a2a] hover:bg-[#333] text-white font-medium rounded-xl transition-colors"
                  >
                    {popupState.cancelText}
                  </button>
                )}
                <button
                  onClick={popupState.onConfirm}
                  className={`flex-1 px-4 py-3 font-bold rounded-xl transition-colors ${
                    popupState.type === 'error' ? 'bg-red-500 hover:bg-red-600 text-white' :
                    'bg-[#0BD9E5] hover:bg-[#09bcc7] text-black'
                  }`}
                >
                  {popupState.confirmText}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </PopupContext.Provider>
  );
};
